import { useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { Settings as SettingsIcon, Plus, Pencil, Trash2, X, Save } from 'lucide-react';
import PageHeader from '../../../components/PageHeader';
import WhsGate from '../../../components/whs/WhsGate';
import Button from '../../../components/ui/Button';
import RiskMatrix from '../../../components/whs/RiskMatrix';
import whsService from '../../../services/whs';
import userService from '../../../services/user';
import type { UserData } from '../../../services/user';
import type { WhsSettings, ResponsiblePerson, ResponsibleRole, RiskMatrix as RiskMatrixConfig } from '../../../types/whs';
import { Badge, RESPONSIBLE_ROLE_LABEL } from '../../../components/whs/whsConstants';
import { useOrgTimezone, formatLocalDateInTz } from '../../../utils/timezone';

const inputCls = 'w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500';
const labelCls = 'block text-xs font-medium text-neutral-600 mb-1';

const orNull = (v: string) => (v.trim() === '' ? null : v.trim());
const userLabel = (u: UserData) => [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username;

function Modal({ title, onClose, children, footer }: { title: string; onClose: () => void; children: ReactNode; footer?: ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-3">
          <h3 className="text-base font-semibold text-neutral-900">{title}</h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600" aria-label="Close"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-5 py-4">{children}</div>
        {footer && <div className="flex justify-end gap-2 border-t border-neutral-200 px-5 py-3">{footer}</div>}
      </div>
    </div>
  );
}

interface GovForm {
  whs_manager_user_id: string;
  policy_statement: string;
  policy_reviewed_date: string;
  policy_next_review: string;
  regulator_name: string;
  regulator_phone: string;
  notifiable_contact: string;
  emergency_contact: string;
}

interface RpForm {
  user_id: string;
  person_name: string;
  role_type: ResponsibleRole;
  work_group: string;
  appointed_date: string;
  expires_date: string;
  contact: string;
  notes: string;
  is_active: boolean;
}

const emptyRpForm: RpForm = {
  user_id: '', person_name: '', role_type: 'whs_manager', work_group: '',
  appointed_date: '', expires_date: '', contact: '', notes: '', is_active: true,
};

function SettingsInner() {
  const orgTz = useOrgTimezone();

  const [persons, setPersons] = useState<ResponsiblePerson[]>([]);
  const [users, setUsers] = useState<UserData[]>([]);
  const [matrix, setMatrix] = useState<RiskMatrixConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [gov, setGov] = useState<GovForm>({
    whs_manager_user_id: '', policy_statement: '', policy_reviewed_date: '', policy_next_review: '',
    regulator_name: '', regulator_phone: '', notifiable_contact: '', emergency_contact: '',
  });
  const [savingGov, setSavingGov] = useState(false);

  const [rpModalOpen, setRpModalOpen] = useState(false);
  const [rpEditing, setRpEditing] = useState<ResponsiblePerson | null>(null);
  const [rpForm, setRpForm] = useState<RpForm>(emptyRpForm);
  const [savingRp, setSavingRp] = useState(false);

  const applySettings = (s: WhsSettings) => {
    setGov({
      whs_manager_user_id: s.whs_manager_user_id != null ? String(s.whs_manager_user_id) : '',
      policy_statement: s.policy_statement ?? '',
      policy_reviewed_date: s.policy_reviewed_date ?? '',
      policy_next_review: s.policy_next_review ?? '',
      regulator_name: s.regulator_name ?? '',
      regulator_phone: s.regulator_phone ?? '',
      notifiable_contact: s.notifiable_contact ?? '',
      emergency_contact: s.emergency_contact ?? '',
    });
    const m = s.risk_matrix ?? s.settings?.risk_matrix ?? null;
    if (m) setMatrix(m);
  };

  const load = useCallback(async () => {
    setLoading(true);
    const [sRes, pRes, uRes, mRes] = await Promise.all([
      whsService.getSettings(),
      whsService.listResponsiblePersons(),
      userService.getUsers(),
      whsService.getRiskMatrix(),
    ]);
    if (sRes.success) applySettings(sRes.data); else setError(sRes.error.message);
    if (pRes.success) setPersons(pRes.data); else setError(pRes.error.message);
    if (uRes.success) setUsers(uRes.data);
    if (mRes.success) {
      setMatrix((prev) => prev ?? mRes.data);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveGov = async () => {
    setSavingGov(true);
    setSuccess(null);
    const payload: Partial<WhsSettings> = {
      whs_manager_user_id: gov.whs_manager_user_id ? Number(gov.whs_manager_user_id) : null,
      policy_statement: orNull(gov.policy_statement),
      policy_reviewed_date: orNull(gov.policy_reviewed_date),
      policy_next_review: orNull(gov.policy_next_review),
      regulator_name: orNull(gov.regulator_name),
      regulator_phone: orNull(gov.regulator_phone),
      notifiable_contact: orNull(gov.notifiable_contact),
      emergency_contact: orNull(gov.emergency_contact),
    };
    const res = await whsService.updateSettings(payload);
    setSavingGov(false);
    if (res.success) { applySettings(res.data); setError(null); setSuccess('Governance settings saved.'); }
    else setError(res.error.message);
  };

  const openRpCreate = () => { setRpEditing(null); setRpForm(emptyRpForm); setRpModalOpen(true); };
  const openRpEdit = (p: ResponsiblePerson) => {
    setRpEditing(p);
    setRpForm({
      user_id: p.user_id != null ? String(p.user_id) : '',
      person_name: p.person_name ?? '',
      role_type: p.role_type,
      work_group: p.work_group ?? '',
      appointed_date: p.appointed_date ?? '',
      expires_date: p.expires_date ?? '',
      contact: p.contact ?? '',
      notes: p.notes ?? '',
      is_active: p.is_active,
    });
    setRpModalOpen(true);
  };

  const submitRp = async () => {
    setSavingRp(true);
    const payload: Partial<ResponsiblePerson> = {
      user_id: rpForm.user_id ? Number(rpForm.user_id) : null,
      person_name: rpForm.user_id ? null : orNull(rpForm.person_name),
      role_type: rpForm.role_type,
      work_group: orNull(rpForm.work_group),
      appointed_date: orNull(rpForm.appointed_date),
      expires_date: orNull(rpForm.expires_date),
      contact: orNull(rpForm.contact),
      notes: orNull(rpForm.notes),
      is_active: rpForm.is_active,
    };
    const res = rpEditing
      ? await whsService.updateResponsiblePerson(rpEditing.id, payload)
      : await whsService.createResponsiblePerson(payload);
    setSavingRp(false);
    if (res.success) {
      setRpModalOpen(false);
      setError(null);
      const pRes = await whsService.listResponsiblePersons();
      if (pRes.success) setPersons(pRes.data);
    } else setError(res.error.message);
  };

  const removeRp = async (p: ResponsiblePerson) => {
    const name = [p.first_name, p.last_name].filter(Boolean).join(' ') || p.person_name || 'this person';
    if (!window.confirm(`Remove ${name} from responsible persons?`)) return;
    const res = await whsService.deleteResponsiblePerson(p.id);
    if (res.success) {
      const pRes = await whsService.listResponsiblePersons();
      if (pRes.success) setPersons(pRes.data);
    } else setError(res.error.message);
  };

  const personName = (p: ResponsiblePerson) =>
    [p.first_name, p.last_name].filter(Boolean).join(' ') || p.person_name || '—';

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="WHS Setup & Roles"
        subtitle="Nominate the WHS manager, HSRs & wardens, policy statement and risk matrix"
        icon={<SettingsIcon className="h-6 w-6 text-white" />}
      />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">{error}</div>
        )}
        {success && (
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-700">{success}</div>
        )}

        {loading ? (
          <p className="text-center text-neutral-400">Loading…</p>
        ) : (
          <>
            {/* Governance */}
            <section className="rounded-xl border border-neutral-200 bg-white p-6">
              <h2 className="text-lg font-semibold text-neutral-900">Governance & policy</h2>
              <p className="mt-1 text-sm text-neutral-500">Nominated WHS manager, policy statement and regulator/emergency contacts.</p>

              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelCls}>Nominated WHS Manager</label>
                  <select className={inputCls} value={gov.whs_manager_user_id} onChange={(e) => setGov({ ...gov, whs_manager_user_id: e.target.value })}>
                    <option value="">— Not nominated —</option>
                    {users.map((u) => <option key={u.id} value={u.id}>{userLabel(u)}</option>)}
                  </select>
                </div>
                <div />
                <div className="sm:col-span-2">
                  <label className={labelCls}>WHS policy statement</label>
                  <textarea rows={5} className={inputCls} value={gov.policy_statement} onChange={(e) => setGov({ ...gov, policy_statement: e.target.value })} />
                </div>
                <div>
                  <label className={labelCls}>Policy reviewed date</label>
                  <input type="date" className={inputCls} value={gov.policy_reviewed_date} onChange={(e) => setGov({ ...gov, policy_reviewed_date: e.target.value })} />
                </div>
                <div>
                  <label className={labelCls}>Policy next review</label>
                  <input type="date" className={inputCls} value={gov.policy_next_review} onChange={(e) => setGov({ ...gov, policy_next_review: e.target.value })} />
                </div>
                <div>
                  <label className={labelCls}>Regulator name</label>
                  <input className={inputCls} value={gov.regulator_name} onChange={(e) => setGov({ ...gov, regulator_name: e.target.value })} />
                </div>
                <div>
                  <label className={labelCls}>Regulator phone</label>
                  <input className={inputCls} value={gov.regulator_phone} onChange={(e) => setGov({ ...gov, regulator_phone: e.target.value })} />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Notifiable-incident contact</label>
                  <textarea rows={2} className={inputCls} value={gov.notifiable_contact} onChange={(e) => setGov({ ...gov, notifiable_contact: e.target.value })} />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Emergency contact</label>
                  <textarea rows={2} className={inputCls} value={gov.emergency_contact} onChange={(e) => setGov({ ...gov, emergency_contact: e.target.value })} />
                </div>
              </div>

              <div className="mt-5 flex justify-end">
                <Button onClick={saveGov} isLoading={savingGov}><Save className="h-4 w-4" /> Save governance</Button>
              </div>
            </section>

            {/* Responsible persons */}
            <section className="rounded-xl border border-neutral-200 bg-white p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-neutral-900">Responsible persons</h2>
                  <p className="mt-1 text-sm text-neutral-500">WHS manager, HSRs, first aiders, fire wardens and committee members.</p>
                </div>
                <Button onClick={openRpCreate}><Plus className="h-4 w-4" /> Add person</Button>
              </div>

              <div className="mt-5 overflow-x-auto rounded-lg border border-neutral-200">
                <table className="min-w-full divide-y divide-neutral-200 text-sm">
                  <thead className="bg-neutral-50 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    <tr>
                      <th className="px-4 py-3">Person</th>
                      <th className="px-4 py-3">Role</th>
                      <th className="px-4 py-3">Work group</th>
                      <th className="px-4 py-3">Appointed</th>
                      <th className="px-4 py-3">Expires</th>
                      <th className="px-4 py-3">Contact</th>
                      <th className="px-4 py-3">Active</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100">
                    {persons.length === 0 ? (
                      <tr><td colSpan={8} className="px-4 py-8 text-center text-neutral-400">No responsible persons appointed.</td></tr>
                    ) : persons.map((p) => (
                      <tr key={p.id} className="hover:bg-neutral-50">
                        <td className="px-4 py-3 font-medium text-neutral-900">{personName(p)}</td>
                        <td className="px-4 py-3 text-neutral-700">{RESPONSIBLE_ROLE_LABEL[p.role_type]}</td>
                        <td className="px-4 py-3 text-neutral-600">{p.work_group || '—'}</td>
                        <td className="px-4 py-3 text-neutral-600">{p.appointed_date ? formatLocalDateInTz(p.appointed_date, orgTz) : '—'}</td>
                        <td className="px-4 py-3 text-neutral-600">{p.expires_date ? formatLocalDateInTz(p.expires_date, orgTz) : '—'}</td>
                        <td className="px-4 py-3 text-neutral-600">{p.contact || '—'}</td>
                        <td className="px-4 py-3">
                          {p.is_active
                            ? <Badge label="Active" className="bg-emerald-100 text-emerald-800" />
                            : <Badge label="Inactive" className="bg-neutral-200 text-neutral-600" />}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-1">
                            <button onClick={() => openRpEdit(p)} className="rounded p-1.5 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
                            <button onClick={() => removeRp(p)} className="rounded p-1.5 text-neutral-500 hover:bg-red-50 hover:text-red-600" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Risk matrix (read-only) */}
            <section className="rounded-xl border border-neutral-200 bg-white p-6">
              <h2 className="text-lg font-semibold text-neutral-900">Risk matrix</h2>
              <p className="mt-1 text-sm text-neutral-500">
                These likelihood × consequence bands drive risk scoring across assessments. Read-only reference.
              </p>
              <div className="mt-5">
                {matrix
                  ? <RiskMatrix matrix={matrix} />
                  : <p className="text-sm text-neutral-400">No risk matrix configured.</p>}
              </div>
            </section>
          </>
        )}
      </div>

      {rpModalOpen && (
        <Modal
          title={rpEditing ? 'Edit responsible person' : 'Add responsible person'}
          onClose={() => setRpModalOpen(false)}
          footer={(
            <>
              <Button variant="secondary" onClick={() => setRpModalOpen(false)}>Cancel</Button>
              <Button onClick={submitRp} isLoading={savingRp}>{rpEditing ? 'Save changes' : 'Add'}</Button>
            </>
          )}
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Employee</label>
              <select className={inputCls} value={rpForm.user_id} onChange={(e) => setRpForm({ ...rpForm, user_id: e.target.value })}>
                <option value="">— Not a system user —</option>
                {users.map((u) => <option key={u.id} value={u.id}>{userLabel(u)}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Person name (if not a user)</label>
              <input className={inputCls} value={rpForm.person_name} disabled={!!rpForm.user_id} onChange={(e) => setRpForm({ ...rpForm, person_name: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Role</label>
              <select className={inputCls} value={rpForm.role_type} onChange={(e) => setRpForm({ ...rpForm, role_type: e.target.value as ResponsibleRole })}>
                {(Object.entries(RESPONSIBLE_ROLE_LABEL) as [ResponsibleRole, string][]).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Work group</label>
              <input className={inputCls} value={rpForm.work_group} onChange={(e) => setRpForm({ ...rpForm, work_group: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Appointed date</label>
              <input type="date" className={inputCls} value={rpForm.appointed_date} onChange={(e) => setRpForm({ ...rpForm, appointed_date: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Expires date</label>
              <input type="date" className={inputCls} value={rpForm.expires_date} onChange={(e) => setRpForm({ ...rpForm, expires_date: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Contact</label>
              <input className={inputCls} value={rpForm.contact} onChange={(e) => setRpForm({ ...rpForm, contact: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Notes</label>
              <textarea rows={2} className={inputCls} value={rpForm.notes} onChange={(e) => setRpForm({ ...rpForm, notes: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <label className="inline-flex items-center gap-2 text-sm text-neutral-700">
                <input type="checkbox" className="h-4 w-4 rounded border-neutral-300" checked={rpForm.is_active} onChange={(e) => setRpForm({ ...rpForm, is_active: e.target.checked })} />
                Active appointment
              </label>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default function WhsSettings() {
  return <WhsGate><SettingsInner /></WhsGate>;
}
